import { useState, useRef, useEffect, useMemo, type CSSProperties } from 'react';
import { Button, Input, KeywordForm, Modal, StepsDetail, ScrollTopButton } from '@indipia/ui-core';
import { PhotoViewerLayer } from '../components/PhotoViewerLayer';
import { PhotoCard } from '../components/PhotoCard';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { photoApi } from '../api/photoApi';
import { useAppStore } from '@indipia/shared';
import { usePhotoCommonStore } from '../store/photoCommonStore';
import { usePhotoNavStore } from '../store/photoNavStore';
import { useTagCodeMaps } from '../utils/tagCodeMap';

const BASE = '/agents/photo-organizer';

interface PhotoCard {
  id: number;
  thumbUrl: string | null;
  takenAt: string | null;
  registeredAt: string | null;
  date: string;
  tags: string[];
  checked: boolean;
}

interface SiteGroup {
  siteId: number;
  siteName: string;
  total: number;
  photos: PhotoCard[];
}

// 단일 단계 실행취소용 스냅샷 — 직전 작업으로 바뀐 사진들의 이전 태그 + 그 시점의 "신규 태그" 집합
interface TagActionSnapshot {
  tagsByPhoto: Record<number, string[]>;
  newTagKeysBefore: Set<string>;
}

const newTagKey = (photoId: number, tag: string) => `${photoId}::${tag}`;

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}


// ─── StepBar ─────────────────────────────────────────────
// 업로드 3화면 공용 단계 라벨 — 표준 컴포넌트 StepsDetail(D-09 §15.22)로 렌더한다. 최종 3단계.
function StepBar() {
  const steps = [
    { label: '사진업로드', sub: '완료' },
    { label: '현장분류',   sub: '완료' },
    { label: '태그 등록', sub: '공간·공종 태깅' },
  ];

  return (
    <div style={{ background: 'var(--shell-bg)', padding: '0 var(--spacing-11)', flexShrink: 0 }}>
      <StepsDetail steps={steps} current={2} />
    </div>
  );
}

// ─── TagBulkEditMain ──────────────────────────────────────
export function TagBulkEditMain() {
  const navigate = useNavigate();
  const initialSiteGroups    = usePhotoNavStore(s => s.tagEditGroups);
  const batchPhotos          = usePhotoNavStore(s => s.batchPhotos);
  const setTagBulkEditSavedTags = usePhotoNavStore(s => s.setTagBulkEditSavedTags);
  const tagEditReturnPath    = usePhotoNavStore(s => s.tagEditReturnPath);
  const setTagEditReturnPath = usePhotoNavStore(s => s.setTagEditReturnPath);
  // [이전]으로 분류 화면에 복귀할 때만 true — 그 외(완료, 다른 화면 이동 등 모든 언마운트)에는
  // 분류 화면 스냅샷(classifySiteGroups)이 다음 세션에 잘못 복원되지 않도록 정리한다
  const returningToClassifyRef = useRef(false);
  useEffect(() => () => {
    if (!returningToClassifyRef.current) usePhotoNavStore.getState().clearClassifySiteGroups();
  }, []);

  const [groups, setGroups] = useState<SiteGroup[]>([]);
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [viewer, setViewer] = useState<{ photos: { photoId: number; thumbUrl: string | null; originalUrl: string | null; takenAt: string | null; tags: { tagType: string; tagValue: string }[] }[]; initialIndex: number } | null>(null);
  const [tagInput, setTagInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // 마운트 시점의 savedTags만 캡처 — 이후 변경에 반응하지 않도록 ref로 고정
  const savedTagsAtMount = useRef(usePhotoNavStore.getState().tagBulkEditSavedTags);
  // 사진별 최신 태그 상태 — 연속 클릭 시 아직 반영되지 않은 React state 대신 이 ref를 진실원본으로 사용
  const tagsRef = useRef<Record<number, string[]>>({});
  // 이번 화면 진입 후 새로 추가된 태그 키(`${photoId}::${tag}`) 집합 — 기존 태그와 구분 표시용 진실원본
  const newTagKeysRef = useRef<Set<string>>(new Set());
  const [newTagKeys, setNewTagKeys] = useState<Set<string>>(new Set());
  // 직전 1건의 태그 작업(개별 추가/삭제, 일괄 적용, 전체 삭제)을 되돌리기 위한 스냅샷 — 단일 단계 실행취소
  const lastActionRef = useRef<TagActionSnapshot | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  // 일괄 적용/제거 모드 — 실행 1회 후 자동으로 '추가'로 복귀(실수로 계속 제거 모드에 남는 것 방지)
  const [tagMode, setTagMode] = useState<'add' | 'remove'>('add');
  // 모든 태그 PATCH 요청을 하나의 큐로 직렬화 — 동시 요청이 겹치면 BE에서 DB 데드락이 발생한다
  const requestQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const enqueuePatch = <T,>(fn: () => Promise<T>): Promise<T> => {
    const run: Promise<T> = requestQueueRef.current.then(() => fn(), () => fn());
    requestQueueRef.current = run.then(() => undefined, () => undefined);
    return run;
  };

  const commonCodes     = usePhotoCommonStore(s => s.commonCodes);
  const userTags    = usePhotoCommonStore(s => s.userTags);
  const setUserTags = usePhotoCommonStore(s => s.setUserTags);
  const appCodes    = useAppStore(s => s.codes);
  const { spaceCodeByName, workCodeByName } = useTagCodeMaps();

  const spaceTags  = (commonCodes?.['A01_SPACE_TAG'] ?? []).map(c => c.codeName);
  const workTags   = appCodes.filter(c => c.codeGroupCode === 'ZEU_WORK_TYPE').map(c => c.codeName);
  const myTagChips = userTags;
  const userTagIdByName = Object.fromEntries(myTagChips.map(u => [u.tagValue, u.userTagId]));

  const resolveTagType = (tag: string): { tagType: string; tagCode?: string } =>
    spaceTags.includes(tag) ? { tagType: 'SPACE', tagCode: spaceCodeByName[tag] }
    : workTags.includes(tag) ? { tagType: 'WORK_TYPE', tagCode: workCodeByName[tag] }
    : { tagType: 'CUSTOM', tagCode: userTagIdByName[tag] != null ? String(userTagIdByName[tag]) : undefined };

  // 이번 업로드 사진만 표시 — batchPhotos 룩업으로 API 호출 없이 즉시 초기화
  // savedTags가 있으면 이전 세션에 등록된 태그를 복원한다
  useEffect(() => {
    if (!initialSiteGroups?.length) return;
    const batchMap = new Map(batchPhotos.map(p => [p.photoId, { thumbUrl: p.thumbUrl, takenAt: p.takenAt, registeredAt: p.registeredAt ?? null }]));
    const nextGroups = initialSiteGroups.map(g => {
      // batchPhotos(현재 업로드 세션) 없으면 tagEditGroups.thumbPhotos(분류 화면에서 보존한 URL) 사용
      const thumbMap = new Map((g.thumbPhotos ?? []).map(p => [p.photoId, { thumbUrl: p.thumbUrl, takenAt: p.takenAt, registeredAt: p.registeredAt ?? null }]));
      const photos: PhotoCard[] = (g.sessionPhotoIds ?? []).map(id => {
        const info = batchMap.get(id) ?? thumbMap.get(id);
        return { id, thumbUrl: info?.thumbUrl ?? null, takenAt: info?.takenAt ?? null, registeredAt: info?.registeredAt ?? null, date: fmtDate(info?.takenAt ?? null), tags: savedTagsAtMount.current[id] ?? [], checked: true };
      });
      return { siteId: g.siteId, siteName: g.siteName, total: photos.length, photos };
    });
    setGroups(nextGroups);
    tagsRef.current = Object.fromEntries(nextGroups.flatMap(g => g.photos).map(p => [p.id, p.tags]));

    // 같은 사진을 재업로드(덮어쓰기)하면 PHOTO_ID가 재사용되어 서버에 태그가 이미 있을 수 있다.
    // 정상 진입 경로(업로드 배치 응답의 overwrittenTags → UploadMain, 또는 현장상세의
    // /sites/{id}/photos 응답 → SiteDetailMain)는 둘 다 진입 전에 savedTagsAtMount를 채워두므로
    // 여기선 보통 idsToFetch가 비어 API 호출이 안 나간다 — 혹시 그 경로를 안 거친 photoId가
    // 있을 때만 개별 조회로 복원하는 폴백이다.
    const idsToFetch = nextGroups.flatMap(g => g.photos).map(p => p.id)
      .filter(id => savedTagsAtMount.current[id] === undefined);
    if (idsToFetch.length === 0) return;
    const controller = new AbortController();
    Promise.all(idsToFetch.map((id): Promise<readonly [number, string[]]> =>
      photoApi.getPhotoDetail(id, controller.signal)
        .then(res => [id, res.data?.data?.tags?.map(t => t.tagValue) ?? []] as const)
        .catch(() => [id, []] as const)
    )).then(results => {
      if (controller.signal.aborted) return;
      const withTags = results.filter(([, tags]) => tags.length > 0);
      if (withTags.length === 0) return;
      for (const [id, tags] of withTags) tagsRef.current[id] = tags;
      const tagsById = new Map(withTags);
      setGroups(prev => prev.map(g => ({
        ...g, photos: g.photos.map(p => tagsById.has(p.id) ? { ...p, tags: tagsById.get(p.id)! } : p),
      })));
    });
    return () => controller.abort();
  }, [initialSiteGroups, batchPhotos]);

  const allPhotos = groups.flatMap(g => g.photos);
  const checkedCount = allPhotos.filter(p => p.checked).length;

  // photoId → 이번 세션에 새로 추가된 태그 목록 (PhotoCard에 새 태그 스타일 구분용으로 전달)
  const newTagsByPhoto = useMemo(() => {
    const map: Record<number, string[]> = {};
    newTagKeys.forEach(key => {
      const sep = key.indexOf('::');
      const id = Number(key.slice(0, sep));
      const tag = key.slice(sep + 2);
      (map[id] ??= []).push(tag);
    });
    return map;
  }, [newTagKeys]);

  const toggleGroupAll = (siteId: number) => {
    setGroups(prev => prev.map(g => {
      if (g.siteId !== siteId) return g;
      const allChecked = g.photos.every(p => p.checked);
      return { ...g, photos: g.photos.map(p => ({ ...p, checked: !allChecked })) };
    }));
  };

  const togglePhoto = (photoId: number) => {
    setGroups(prev => prev.map(g => ({
      ...g, photos: g.photos.map(p => p.id === photoId ? { ...p, checked: !p.checked } : p),
    })));
  };

  const toTagPayload = (tags: string[]) =>
    tags.map(v => ({ ...resolveTagType(v), tagValue: v }));

  const setPhotoTags = (photoId: number, tags: string[]) => {
    setGroups(prev => prev.map(g => ({
      ...g, photos: g.photos.map(p => p.id === photoId ? { ...p, tags } : p),
    })));
  };

  // 되돌리기 스냅샷 기록 — 이후 태그 작업 함수들이 실제 변경 전에 호출한다
  const recordUndoSnapshot = (photoIds: number[]) => {
    const tagsByPhoto: Record<number, string[]> = {};
    photoIds.forEach(id => { tagsByPhoto[id] = tagsRef.current[id] ?? []; });
    lastActionRef.current = { tagsByPhoto, newTagKeysBefore: new Set(newTagKeysRef.current) };
    setCanUndo(true);
  };

  const removeTag = (photoId: number, tag: string) => {
    const prevTags = tagsRef.current[photoId] ?? [];
    const nextTags = prevTags.filter(t => t !== tag);
    const prevNewKeys = new Set(newTagKeysRef.current);
    recordUndoSnapshot([photoId]);

    tagsRef.current[photoId] = nextTags;
    setPhotoTags(photoId, nextTags);
    newTagKeysRef.current.delete(newTagKey(photoId, tag));
    setNewTagKeys(new Set(newTagKeysRef.current));

    enqueuePatch(() => photoApi.patchPhotoTags(photoId, toTagPayload(nextTags))).catch(() => {
      tagsRef.current[photoId] = prevTags;
      setPhotoTags(photoId, prevTags);
      newTagKeysRef.current = prevNewKeys;
      setNewTagKeys(new Set(prevNewKeys));
      lastActionRef.current = null;
      setCanUndo(false);
      toast.error('태그 삭제에 실패했습니다');
    });
  };

  const commitInlineTag = (photoId: number, value: string) => {
    const t = value.trim();
    if (!t) return;
    const prevTags = tagsRef.current[photoId] ?? [];
    if (prevTags.includes(t)) return;
    if (prevTags.length >= 10) { toast.error('사진 하나에는 태그를 최대 10개까지 추가할 수 있어요'); return; }
    const nextTags = [...prevTags, t];
    const prevNewKeys = new Set(newTagKeysRef.current);
    recordUndoSnapshot([photoId]);

    tagsRef.current[photoId] = nextTags;
    setPhotoTags(photoId, nextTags);
    newTagKeysRef.current.add(newTagKey(photoId, t));
    setNewTagKeys(new Set(newTagKeysRef.current));

    enqueuePatch(() => photoApi.patchPhotoTags(photoId, toTagPayload(nextTags))).catch(() => {
      tagsRef.current[photoId] = prevTags;
      setPhotoTags(photoId, prevTags);
      newTagKeysRef.current = prevNewKeys;
      setNewTagKeys(new Set(prevNewKeys));
      lastActionRef.current = null;
      setCanUndo(false);
      toast.error('태그 저장에 실패했습니다');
    });
  };

  // 여러 태그를 연속으로 클릭해도 항상 tagsRef(최신 상태) 기준으로 계산하고,
  // 모든 사진의 PATCH를 enqueuePatch 큐 하나로 직렬화해 동시 요청으로 인한 DB 데드락을 막는다
  const applyBulkTag = (tag?: string) => {
    const t = (tag ?? tagInput).trim();
    if (!t || checkedCount === 0) return;
    const checkedPhotos = allPhotos.filter(p => p.checked);
    const targets = checkedPhotos.filter(p => !(tagsRef.current[p.id] ?? p.tags).includes(t) && (tagsRef.current[p.id] ?? p.tags).length < 10);
    if (!targets.length) {
      const allAtLimit = checkedPhotos.every(p => (tagsRef.current[p.id] ?? p.tags).length >= 10);
      toast.error(allAtLimit
        ? '사진 하나에는 태그를 최대 10개까지 추가할 수 있어요'
        : '선택한 사진에 이미 추가된 태그예요');
      return;
    }
    setTagInput('');
    inputRef.current?.focus();
    recordUndoSnapshot(targets.map(p => p.id));

    let hasError = false;
    targets.forEach(p => {
      const prevTags = tagsRef.current[p.id] ?? p.tags;
      const nextTags = [...prevTags, t];
      tagsRef.current[p.id] = nextTags;
      setPhotoTags(p.id, nextTags);
      newTagKeysRef.current.add(newTagKey(p.id, t));
      enqueuePatch(() => photoApi.patchPhotoTags(p.id, toTagPayload(nextTags))).catch(() => {
        tagsRef.current[p.id] = prevTags;
        setPhotoTags(p.id, prevTags);
        newTagKeysRef.current.delete(newTagKey(p.id, t));
        setNewTagKeys(new Set(newTagKeysRef.current));
        if (!hasError) {
          hasError = true;
          toast.error('태그 저장에 실패했습니다');
        }
      });
    });
    setNewTagKeys(new Set(newTagKeysRef.current));
  };

  // applyBulkTag의 반대짝 — 체크된 사진 중 해당 태그를 가진 것에서만 제거.
  // 대상이 없으면(오타 등) 사전 검사 없이 토스트로만 안내한다.
  const removeBulkTag = (tag?: string) => {
    const t = (tag ?? tagInput).trim();
    if (!t || checkedCount === 0) return;
    const checkedPhotos = allPhotos.filter(p => p.checked);
    const targets = checkedPhotos.filter(p => (tagsRef.current[p.id] ?? p.tags).includes(t));
    if (!targets.length) {
      toast.error(`선택한 사진에 '${t}' 태그가 없어요`);
      return;
    }
    setTagInput('');
    inputRef.current?.focus();
    recordUndoSnapshot(targets.map(p => p.id));

    let hasError = false;
    targets.forEach(p => {
      const prevTags = tagsRef.current[p.id] ?? p.tags;
      const nextTags = prevTags.filter(x => x !== t);
      const wasNew = newTagKeysRef.current.has(newTagKey(p.id, t));
      tagsRef.current[p.id] = nextTags;
      setPhotoTags(p.id, nextTags);
      newTagKeysRef.current.delete(newTagKey(p.id, t));
      enqueuePatch(() => photoApi.patchPhotoTags(p.id, toTagPayload(nextTags))).catch(() => {
        tagsRef.current[p.id] = prevTags;
        setPhotoTags(p.id, prevTags);
        if (wasNew) newTagKeysRef.current.add(newTagKey(p.id, t));
        setNewTagKeys(new Set(newTagKeysRef.current));
        if (!hasError) {
          hasError = true;
          toast.error('태그 제거에 실패했습니다');
        }
      });
    });
    setNewTagKeys(new Set(newTagKeysRef.current));
  };

  // 자주 쓰는 태그/나만의 태그 칩 클릭 — tagMode에 따라 추가 또는 제거를 실행하고, 제거는 1회성으로 모드를 되돌린다
  const handleQuickTagSelect = (tag: string) => {
    if (tagMode === 'remove') {
      removeBulkTag(tag);
      setTagMode('add');
    } else {
      applyBulkTag(tag);
    }
  };

  // 현재 체크된 사진 중 태그가 있는 것만 대상으로 전체 삭제 (일괄 적용과 동일한 선택 범위)
  const clearCheckedTags = () => {
    setClearConfirmOpen(false);
    const targets = allPhotos.filter(p => p.checked && (tagsRef.current[p.id] ?? p.tags).length > 0);
    if (!targets.length) return;
    recordUndoSnapshot(targets.map(p => p.id));

    let hasError = false;
    targets.forEach(p => {
      const prevTags = tagsRef.current[p.id] ?? p.tags;
      tagsRef.current[p.id] = [];
      setPhotoTags(p.id, []);
      prevTags.forEach(tag => newTagKeysRef.current.delete(newTagKey(p.id, tag)));
      enqueuePatch(() => photoApi.patchPhotoTags(p.id, [])).catch(() => {
        tagsRef.current[p.id] = prevTags;
        setPhotoTags(p.id, prevTags);
        if (!hasError) {
          hasError = true;
          toast.error('태그 삭제에 실패했습니다');
        }
      });
    });
    setNewTagKeys(new Set(newTagKeysRef.current));
    toast.success(`선택한 사진 ${targets.length}장의 태그를 모두 삭제했습니다`);
  };

  // 직전 1건의 태그 작업을 되돌린다 (단일 단계) — 복원 후 서버에도 다시 반영
  const undoLastAction = () => {
    const snapshot = lastActionRef.current;
    if (!snapshot) return;
    lastActionRef.current = null;
    setCanUndo(false);

    const affectedIds = Object.keys(snapshot.tagsByPhoto).map(Number);
    affectedIds.forEach(id => { tagsRef.current[id] = snapshot.tagsByPhoto[id]; });
    setGroups(prev => prev.map(g => ({
      ...g, photos: g.photos.map(p => affectedIds.includes(p.id) ? { ...p, tags: snapshot.tagsByPhoto[p.id] } : p),
    })));
    newTagKeysRef.current = new Set(snapshot.newTagKeysBefore);
    setNewTagKeys(new Set(snapshot.newTagKeysBefore));

    let hasError = false;
    affectedIds.forEach(id => {
      enqueuePatch(() => photoApi.patchPhotoTags(id, toTagPayload(snapshot.tagsByPhoto[id]))).catch(() => {
        if (!hasError) {
          hasError = true;
          toast.error('되돌리기 중 일부 저장에 실패했습니다');
        }
      });
    });
    toast.success('직전 작업을 되돌렸습니다');
  };

  const deleteMyTag = async (tagValue: string) => {
    const snap = myTagChips;
    const nextTags = myTagChips.filter(t => t.tagValue !== tagValue);
    setUserTags(nextTags);
    try {
      await photoApi.saveUserTags(nextTags.map(t => t.tagValue));
      const res = await photoApi.getUserTags();
      setUserTags(res.data?.data?.tags ?? nextTags);
    } catch {
      setUserTags(snap);
    }
  };

  // 빈 값·중복·10개 초과는 KeywordForm 이 걸러 경고한다
  const saveMyTag = async (t: string) => {
    const snap = myTagChips;
    const nextValues = [...myTagChips.map(chip => chip.tagValue), t];
    try {
      await photoApi.saveUserTags(nextValues);
      const res = await photoApi.getUserTags();
      setUserTags(res.data?.data?.tags ?? snap);
    } catch {
      setUserTags(snap);
    }
  };

  return (
    <>
      {!tagEditReturnPath && <StepBar />}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', minHeight: 0 }}>

        {/* ── 스크롤 영역: 현장별 그룹 카드 ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 16px 20px', background: 'var(--shell-bg)', minHeight: 0 }}>
          {/* 스크롤 맨 위로(D-09 §15.55) — 이 div 가 스크롤 소유자라 main 의 버튼이 반응하지 않는다. 직속 자식이라 스스로 이 div 를 찾는다 */}
          <ScrollTopButton />
          {groups.map(group => {
            const groupAllChecked = group.photos.length > 0 && group.photos.every(p => p.checked);
            const groupCheckedCount = group.photos.filter(p => p.checked).length;
            const isCollapsed = collapsed[group.siteId] ?? false;

            return (
              <div key={group.siteId} style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--shell-border)', boxShadow: '0 2px 10px var(--alpha-black-50)', marginBottom: 14, overflow: 'hidden' }}>

                {/* 현장 헤더 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', borderBottom: isCollapsed ? 'none' : '1px solid var(--shell-border)' }}>
                  <button
                    aria-label="현장 사진 전체 선택"
                    aria-pressed={groupAllChecked}
                    onClick={() => toggleGroupAll(group.siteId)}
                    style={{
                      width: 18, height: 18, borderRadius: 'var(--radius-sm)', flexShrink: 0, cursor: 'pointer',
                      border: `2px solid ${groupAllChecked ? 'var(--gt-primary)' : 'var(--shell-hint)'}`,
                      background: groupAllChecked ? 'var(--gt-primary)' : 'var(--shell-surface)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .12s',
                      padding: 0, fontFamily: 'inherit',
                    }}
                  >
                    {groupAllChecked && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                  </button>
                  <div style={{ width: 18, height: 18, borderRadius: 'var(--radius-full)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.3px' }}>{group.siteName}</span>
                  <div style={{ flex: 1 }} />
                  {groupCheckedCount > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--gt-primary)', background: 'color-mix(in srgb, var(--gt-primary) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)', borderRadius: 'var(--radius-sm)', padding: '2px 8px' }}>
                      {groupCheckedCount}장 선택됨
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--shell-hint)', fontWeight: 600 }}>이번 업로드 {group.total}장</span>
                  <button
                    onClick={() => setCollapsed(prev => ({ ...prev, [group.siteId]: !isCollapsed }))}
                    aria-label={isCollapsed ? '펼치기' : '접기'}
                    style={{ width: 24, height: 24, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .2s' }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>

                {/* 사진 그리드 */}
                {!isCollapsed && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, padding: '12px 16px' }}>
                  {group.photos.map((photo, pi) => {
                    const groupOffset = groups.slice(0, groups.indexOf(group)).reduce((acc, g) => acc + g.photos.length, 0);
                    return (
                      <PhotoCard
                        key={photo.id}
                        photoId={photo.id}
                        thumbUrl={photo.thumbUrl}
                        takenAt={photo.takenAt}
                        registeredAt={photo.registeredAt}
                        tags={photo.tags}
                        newTags={newTagsByPhoto[photo.id]}
                        checked={photo.checked}
                        onToggle={() => togglePhoto(photo.id)}
                        onRemoveTag={(tag) => removeTag(photo.id, tag)}
                        onAddTag={(tag) => commitInlineTag(photo.id, tag)}
                        onViewPhoto={() => setViewer({
                          photos: allPhotos.map(p => ({
                            photoId: p.id,
                            thumbUrl: p.thumbUrl,
                            originalUrl: null as string | null,
                            takenAt: p.takenAt,
                            tags: p.tags.map(t => ({ tagType: resolveTagType(t).tagType, tagValue: t })),
                          })),
                          initialIndex: groupOffset + pi,
                        })}
                      />
                    );
                  })}
                </div>}
              </div>
            );
          })}
        </div>

        {/* ── 우측 패널 (자주 쓰는 태그) ── */}
        <div style={{ width: 'min(300px, 28vw)', flexShrink: 0, background: 'var(--shell-bg)', padding: '14px 20px 16px 0', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <div style={{
            flex: 1, minHeight: 0, background: 'var(--shell-surface)', borderRadius: 'var(--radius-lg)', border: '1.5px solid var(--shell-border)',
            boxShadow: '0 4px 20px var(--alpha-black-100)', padding: '14px 16px 16px',
            overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {/* 태그 입력 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-hint)' }}>
                {checkedCount > 0 ? <span style={{ color: 'var(--gt-primary)', fontWeight: 700 }}>{checkedCount}장 선택됨</span> : '선택 없음'}
              </span>
              {/* 추가/제거 모드 — 아래 입력·버튼과 '자주 쓰는 태그' 칩 클릭에 공통 적용 */}
              <div style={{ position: 'relative', display: 'flex', background: 'var(--shell-skeleton)', borderRadius: 'var(--radius-md)', padding: 3 }}>
                <div
                  aria-hidden
                  style={{
                    position: 'absolute', top: 3, bottom: 3, left: 3, width: 'calc(50% - 3px)', borderRadius: 'var(--radius-sm)',
                    background: tagMode === 'remove' ? 'var(--color-error)' : 'var(--shell-surface)',
                    boxShadow: '0 1px 3px var(--alpha-black-100)',
                    transform: tagMode === 'remove' ? 'translateX(100%)' : 'translateX(0)',
                    transition: 'transform .2s cubic-bezier(.4,0,.2,1), background .2s',
                  }}
                />
                <button
                  onClick={() => setTagMode('add')}
                  style={{
                    position: 'relative', zIndex: 1, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    padding: '6px 0', borderRadius: 'var(--radius-sm)', border: 'none', background: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700, fontFamily: 'inherit', transition: 'color .2s',
                    color: tagMode === 'add' ? 'var(--shell-on-surface)' : 'var(--shell-hint)',
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  추가
                </button>
                <button
                  onClick={() => setTagMode('remove')}
                  style={{
                    position: 'relative', zIndex: 1, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    padding: '6px 0', borderRadius: 'var(--radius-sm)', border: 'none', background: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700, fontFamily: 'inherit', transition: 'color .2s',
                    color: tagMode === 'remove' ? 'var(--gray-0)' : 'var(--shell-hint)',
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  제거
                </button>
              </div>
              <Input
                ref={inputRef}
                size="sm"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key !== 'Enter') return; if (tagMode === 'remove') { removeBulkTag(); setTagMode('add'); } else { applyBulkTag(); } }}
                placeholder={tagMode === 'remove' ? '제거할 태그 입력' : '태그 입력 (예: 거실)'}
                maxLength={10}
              />
              <Button
                variant={tagMode === 'remove' ? 'danger' : 'secondary'}
                size="sm"
                className="w-full"
                disabled={!(checkedCount > 0 && tagInput.trim())}
                onClick={() => { if (tagMode === 'remove') { removeBulkTag(); setTagMode('add'); } else { applyBulkTag(); } }}
              >
                {tagMode === 'remove' ? (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /><line x1="9" y1="14" x2="15" y2="14" />
                  </svg>
                ) : (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" />
                  </svg>
                )}
                {tagMode === 'remove' ? '일괄 제거' : '일괄 적용'}
              </Button>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                  style={{ minWidth: 0 }}
                  disabled={!canUndo}
                  onClick={undoLastAction}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                  </svg>
                  실행취소
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="flex-1"
                  style={{ minWidth: 0 }}
                  disabled={checkedCount === 0}
                  onClick={() => setClearConfirmOpen(true)}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
                  </svg>
                  전체삭제
                </Button>
              </div>
              <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 'var(--radius-full)', background: 'var(--color-successSubtle)', border: '1.5px solid var(--color-success)' }} />
                초록색으로 표시된 태그는 이번에 새로 등록한 태그예요
              </span>
            </div>

            <div style={{ borderTop: '1px solid var(--shell-border)', margin: '0 -16px' }} />

            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--gt-primary)', letterSpacing: '-0.2px', display: 'flex', alignItems: 'center', gap: 6 }}>
              자주 쓰는 태그
              {tagMode === 'remove' && (
                <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--color-error)' }}>· 클릭하면 제거돼요</span>
              )}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <QuickTagRow label="공간" tags={spaceTags} onSelect={handleQuickTagSelect} mode={tagMode} />
              <QuickTagRow label="공종" tags={workTags} onSelect={handleQuickTagSelect} mode={tagMode} />

              {/* 나만의 태그 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-hint)' }}>나만의 태그</span>
                <KeywordForm
                  tags={myTagChips.map(chip => chip.tagValue)}
                  onAdd={saveMyTag}
                  onRemove={i => deleteMyTag(myTagChips[i].tagValue)}
                  onTagClick={tag => handleQuickTagSelect(tag)}
                  maxCount={10}
                  maxLength={10}
                  placeholder="태그 입력 후 엔터"
                  help="칩 클릭: 선택한 사진에 적용 · 10개까지 등록"
                />
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── 네비 바 ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', borderTop: '1px solid var(--shell-border)', background: 'var(--shell-surface)', flexShrink: 0 }}>
        {tagEditReturnPath ? <div /> : (
          <Button
            variant="secondary"
            onClick={() => {
              const tagsMap: Record<number, string[]> = {};
              groups.forEach(g => g.photos.forEach(p => { if (p.tags.length) tagsMap[p.id] = p.tags; }));
              setTagBulkEditSavedTags(tagsMap);
              returningToClassifyRef.current = true;
              navigate(`${BASE}/classify-site`);
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            이전
          </Button>
        )}
        <Button
          variant="primary"
          style={{ boxShadow: '0 3px 14px color-mix(in srgb, var(--gt-primary) 35%, transparent)' }}
          onClick={() => {
            if (tagEditReturnPath) {
              const path = tagEditReturnPath;
              setTagEditReturnPath(null);
              navigate(path);
              return;
            }
            navigate(`${BASE}/site-list`);
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          완료
        </Button>
      </div>

      {viewer && (
        <PhotoViewerLayer
          photos={viewer.photos}
          initialIndex={viewer.initialIndex}
          onClose={() => setViewer(null)}
        />
      )}

      {/* 선택 태그 전체삭제 확인 모달 */}
      <Modal open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '10px 4px' }}>
          <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--color-error) 8%, transparent)', border: '1.5px solid color-mix(in srgb, var(--color-error) 25%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </div>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>선택한 {checkedCount}장의 태그를 모두 삭제할까요?</div>
            <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', lineHeight: 1.6 }}>삭제 직후에는 &apos;실행취소&apos;로 되돌릴 수 있습니다.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 4 }}>
            <Button variant="primary" className="flex-1" onClick={() => setClearConfirmOpen(false)}>취소</Button>
            <Button variant="etc" className="flex-1" onClick={clearCheckedTags}>전체삭제</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ─── QuickTagRow ──────────────────────────────────────────
const qChip: CSSProperties = {
  padding: '2px 9px', border: '1px solid var(--shell-border)', borderRadius: 'var(--radius-full)',
  fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface-2)', background: 'var(--shell-bg)',
  cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color .1s',
};

function QuickTagRow({ label, tags, onSelect, mode = 'add' }: { label: string; tags: string[]; onSelect: (t: string) => void; mode?: 'add' | 'remove' }) {
  const hoverColor = mode === 'remove' ? 'var(--color-error)' : 'var(--gt-primary)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-hint)' }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {tags.map(t => (
          <button
            key={t}
            onClick={() => onSelect(t)}
            style={qChip}
            onMouseOver={e => { Object.assign((e.currentTarget as HTMLElement).style, { borderColor: hoverColor, background: mode === 'remove' ? 'color-mix(in srgb, var(--color-error) 10%, transparent)' : 'var(--gt-subtle)' }); }}
            onMouseOut={e => { Object.assign((e.currentTarget as HTMLElement).style, { borderColor: 'var(--shell-border)', background: 'var(--shell-bg)' }); }}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}
